import {
  AddShoppingCart,
  CheckCircle as CheckCircleIcon,
  EditNote as EditNoteIcon,
  Inventory,
  Cancel as CancelIcon,
  PersonAddAlt1,
  RestorePageOutlined as RestorePageIcon,
} from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Chip,
  ChipProps,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Tooltip,
} from "@mui/material";
import React from "react";
import { NavigateFunction } from "react-router-dom";
import {
  CartSingleton,
  SessionExpiredError,
} from "../SharedComponents/CartSingleton";
import { withNavigationHooks } from "../SharedComponents/withNavigationHooks";
import { IAssignment } from "./controller";

export interface ManagedAssignmentRowDelegate {
  didClickArchive(applicationId: number): void;
  didClickCopyAssignment(applicationId: number): void;
  didClickCopyAnonAssignment(applicationId: number): void;
  didClickCopyGenericAnonAssignment(applicationId: number): void;
  didClickReimburse(applicationId: number): void;
  didClickEdit(applicationId: number): void;
  didClickAllocateRespondents(applicationId: number): void;
  applicationShouldLogout(): void;
  didFailAddToCart(message: string): void;
}

interface Props {
  assignment: IAssignment;
  delegate: ManagedAssignmentRowDelegate;
  navigationHooks: NavigateFunction;
}

interface State {
  anchorEl: null | HTMLElement;
  isAddingToCart: boolean;
}

interface DueStatus {
  color: ChipProps["color"];
  variant: ChipProps["variant"];
  label: string;
}

const MS_PER_HOUR = 36e5;
const MS_PER_DAY = MS_PER_HOUR * 24;

function getDueStatus(dueDate: Date): DueStatus {
  const remainingMs = dueDate.getTime() - Date.now();
  const remainingHours = remainingMs / MS_PER_HOUR;
  const remainingDays = remainingMs / MS_PER_DAY;

  if (remainingMs <= 0) {
    return { color: "error", variant: "filled", label: "Expirado" };
  }
  if (remainingHours < 24) {
    return {
      color: "error",
      variant: "outlined",
      label: `Vence em ${Math.ceil(remainingHours)}h`,
    };
  }
  if (remainingDays < 7) {
    return {
      color: "warning",
      variant: "outlined",
      label: `Vence em ${Math.ceil(remainingDays)}d`,
    };
  }
  return {
    color: "default",
    variant: "outlined",
    label: dueDate.toLocaleString(undefined, {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
}

export class ManagedAssignmentRow extends React.Component<Props, State> {
  private isUnmounted = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      anchorEl: null,
      isAddingToCart: false,
    };
  }

  componentDidMount(): void {
    this.isUnmounted = false;
  }

  componentWillUnmount(): void {
    this.isUnmounted = true;
  }

  handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleMenuClose = () => {
    this.setState({ anchorEl: null });
  };

  handleMenuItemClick = (action: () => void) => {
    this.handleMenuClose();
    action();
  };

  /**
   * Ensures the instrument sits in the cart, then goes to checkout — the
   * navigation waits for the write, so a refusal is reported instead of
   * landing the user on checkout without the item.
   */
  async didClickAddToCart(): Promise<void> {
    if (!this.props.assignment?.instrumentId) {
      this.props.navigationHooks("/checkout/review");
      return;
    }
    if (this.state.isAddingToCart) {
      return;
    }

    this.setState({ isAddingToCart: true });
    try {
      // Derive from a post-load read: against an invalidated cache the state
      // copy reads 0, and writing it downgrades a held quantity.
      await CartSingleton.shared.ensureLoaded();
      const item = CartSingleton.shared.getCartInstrument(
        this.props.assignment.instrumentId
      );
      const quantity = item && item.quantity > 0 ? item.quantity : 1;
      await CartSingleton.shared.setCartInstrument(
        this.props.assignment.instrumentId,
        quantity
      );
      if (this.isUnmounted) {
        return;
      }
      this.props.navigationHooks("/checkout/review");
    } catch (error: any) {
      console.warn("Cart add failed", error);
      if (this.isUnmounted) {
        return;
      }
      if (error instanceof SessionExpiredError) {
        this.props.delegate.applicationShouldLogout();
        return;
      }
      // 400 and 403 are the guard statuses whose bodies carry pt-BR user copy;
      // every other body is an internal diagnostic.
      const status = error?.response?.status;
      const message =
        (status === 400 || status === 403) && error?.response?.data?.message
          ? error.response.data.message
          : "Não foi possível adicionar o instrumento ao carrinho.";
      this.props.delegate.didFailAddToCart(message);
    } finally {
      if (!this.isUnmounted) {
        this.setState({ isAddingToCart: false });
      }
    }
  }

  render() {
    const { assignment, delegate } = this.props;
    const menuOpen = Boolean(this.state.anchorEl);

    return (
      <TableRow>
        <TableCell>{assignment.title}</TableCell>
        <TableCell align="right">
          {(() => {
            const dueStatus = getDueStatus(assignment.dueDate);
            return (
              <Tooltip
                slotProps={{
                  tooltip: {
                    sx: {
                      background: "#e4e8ef",
                      color: "#5d7494ff",
                      fontSize: "0.75rem",
                    },
                  },
                  arrow: {
                    sx: { color: "#e4e8ef" },
                  },
                }}
                title={assignment.dueDate.toLocaleString()}>
                <Chip
                  size="small"
                  variant={dueStatus.variant}
                  color={dueStatus.color}
                  label={dueStatus.label}
                />
              </Tooltip>
            );
          })()}
        </TableCell>
        <TableCell align="right">{assignment.instrumentTitle}</TableCell>
        <TableCell align="right">
          {assignment.totalApplicationCount ?? "∞"}
        </TableCell>
        <TableCell align="right">{assignment.doneApplicationCount}</TableCell>
        <TableCell align="right">
          {assignment.notDoneApplicationCount}
        </TableCell>
        <TableCell align="right">
          {assignment.applicationAllowFeedback ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <CancelIcon color="error" fontSize="small" />
          )}
        </TableCell>
        <TableCell align="right">
          <IconButton
            aria-label="actions"
            size="medium"
            sx={{
              backgroundColor: "grey.200",
              color: "primary.main",
              "&:hover": {
                backgroundColor: "grey.300",
              },
            }}
            onClick={this.handleMenuClick}
            title="Mais opções"
          >
            {this.state.isAddingToCart ? (
              <CircularProgress size={20} />
            ) : (
              <MoreVertIcon />
            )}
          </IconButton>
          <Menu
            anchorEl={this.state.anchorEl}
            open={menuOpen}
            onClose={this.handleMenuClose}
          >
            {assignment.totalApplicationCount > 0 &&
              assignment.allowAuthenticatedLink && (
                <MenuItem
                  onClick={() =>
                    this.handleMenuItemClick(() =>
                      delegate.didClickCopyAssignment(assignment.applicationId)
                    )
                  }
                >
                  <ListItemIcon>
                    <ContentCopyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Copiar link identificado" />
                </MenuItem>
              )}

            {assignment.totalApplicationCount > 0 &&
              assignment.allowAnonymousLink && (
                <MenuItem
                  onClick={() =>
                    this.handleMenuItemClick(() =>
                      delegate.didClickCopyGenericAnonAssignment(
                        assignment.applicationId
                      )
                    )
                  }
                >
                  <ListItemIcon>
                    <LinkOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Copiar link anônimo" />
                </MenuItem>
              )}

            <MenuItem
              onClick={() =>
                this.handleMenuItemClick(() =>
                  delegate.didClickEdit(assignment.applicationId)
                )
              }
            >
              <ListItemIcon>
                <EditNoteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Editar aplicação" />
            </MenuItem>

            {assignment.totalApplicationCount > 0 && (
              <MenuItem
                onClick={() =>
                  this.handleMenuItemClick(() =>
                    delegate.didClickAllocateRespondents(assignment.applicationId)
                  )
                }
              >
                <ListItemIcon>
                  <PersonAddAlt1 fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Alocar respondentes" />
              </MenuItem>
            )}

            {assignment.totalApplicationCount < 1 && (
              <MenuItem
                onClick={() =>
                  this.handleMenuItemClick(() => {
                    void this.didClickAddToCart();
                  })
                }
                disabled={this.state.isAddingToCart}
              >
                <ListItemIcon>
                  <AddShoppingCart fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Comprar mais licenças" />
              </MenuItem>
            )}

            <Divider />

            {assignment.notDoneApplicationCount > 0 && (
              <MenuItem
                onClick={() =>
                  this.handleMenuItemClick(() =>
                    delegate.didClickReimburse(assignment.applicationId)
                  )
                }
                disabled={assignment.notDoneApplicationCount === 0}
              >
                <ListItemIcon>
                  <RestorePageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Ressarcir licenças" />
              </MenuItem>
            )}

            <MenuItem
              onClick={() =>
                this.handleMenuItemClick(() =>
                  delegate.didClickArchive(assignment.applicationId)
                )
              }
            >
              <ListItemIcon>
                <Inventory fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Arquivar aplicações" />
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
    );
  }
}

export default withNavigationHooks(ManagedAssignmentRow);
